/**
 * Atlas PM · P8.1 「从模板新建」· 模板选择弹窗。
 *
 * 位置：新建任务抽屉（TaskFormDrawer）标题行的「从模板新建」按钮唤起。
 * 行为：搜索 / 列表 → 选中 → 确认 → 拉取模板详情 → 把 structure 交给 TaskDesigner 回填。
 *
 * ⚠️ 仅新建模式可用（编辑模式由调用方不渲染入口）。
 * ⚠️ 只把「指标 + 要素」结构回填；完成度等运行期字段不填，任务形态一并带入，其余留空。
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import Spinner from '../../components/Spinner';
import { ApiError } from '../../api/client';
import {
  taskTemplateApi,
  templateListKey,
  type TaskTemplateListItem,
  type TemplateStructure,
} from './taskTemplates';

export interface TemplatePickerDialogProps {
  open: boolean;
  onClose(): void;
  /** 选中并确认后：把模板结构 + 形态交给调用方回填表单 */
  onSelect(structure: TemplateStructure, categoryId: string | null): void;
}

export default function TemplatePickerDialog({
  open,
  onClose,
  onSelect,
}: TemplatePickerDialogProps) {
  const [keyword, setKeyword] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState('');

  const params = useMemo(() => ({ keyword: keyword.trim() || undefined, page: 1, pageSize: 50 }), [
    keyword,
  ]);

  const listQuery = useQuery({
    queryKey: templateListKey(params),
    queryFn: () => taskTemplateApi.list(params),
    enabled: open,
    staleTime: 10_000,
  });

  // 每次打开重置交互态
  useEffect(() => {
    if (!open) return;
    setKeyword('');
    setSelectedId(null);
    setApplying(false);
    setApplyError('');
  }, [open]);

  const items = listQuery.data?.items ?? [];

  const confirm = async () => {
    if (!selectedId || applying) return;
    setApplyError('');
    setApplying(true);
    try {
      const detail = await taskTemplateApi.detail(selectedId);
      onSelect(detail.structure, detail.categoryId);
      onClose();
    } catch (err) {
      setApplyError(err instanceof ApiError ? err.message : '套用模板失败');
      setApplying(false);
    }
  };

  return (
    <Modal
      open={open}
      title="从模板新建"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={applying}>
            取消
          </Button>
          <Button
            variant="primary"
            onClick={confirm}
            disabled={!selectedId || applying}
            data-testid="template-use"
          >
            {applying ? '套用中…' : '使用该模板'}
          </Button>
        </>
      }
    >
      <input
        className="td-input"
        data-testid="template-search"
        placeholder="搜索模板名称"
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
      />

      <div className="tt-list" data-testid="template-list">
        {listQuery.isLoading ? (
          <div className="tt-empty">
            <Spinner label="加载模板…" />
          </div>
        ) : listQuery.isError ? (
          <div className="tt-empty" data-testid="template-list-error">
            模板加载失败：
            {listQuery.error instanceof Error ? listQuery.error.message : '请稍后重试'}
          </div>
        ) : items.length === 0 ? (
          <div className="tt-empty" data-testid="template-empty">
            暂无可用模板
          </div>
        ) : (
          items.map((t) => (
            <TemplateRow
              key={t.id}
              item={t}
              selected={selectedId === t.id}
              onSelect={() => setSelectedId(t.id)}
            />
          ))
        )}
      </div>

      {applyError && (
        <div className="pm-modal__err" data-testid="template-apply-error">
          ⚠ {applyError}
        </div>
      )}
    </Modal>
  );
}

function TemplateRow({
  item,
  selected,
  onSelect,
}: {
  item: TaskTemplateListItem;
  selected: boolean;
  onSelect(): void;
}) {
  const isPublic = item.scope === 'PUBLIC';
  return (
    <button
      type="button"
      className={`tt-item${selected ? ' is-sel' : ''}`}
      data-testid="template-item"
      data-template-id={item.id}
      onClick={onSelect}
    >
      <div className="tt-item__main">
        <span className="tt-item__name">{item.name}</span>
        <span className={`tt-badge tt-badge--${isPublic ? 'pub' : 'mine'}`}>
          {isPublic ? '公共' : '个人'}
        </span>
      </div>
      {item.description && <div className="tt-item__desc">{item.description}</div>}
      <div className="tt-item__meta">
        {item.metricCount} 个指标 · {item.elementCount} 个要素
        {item.createdByName ? ` · ${item.createdByName}` : ''}
      </div>
    </button>
  );
}
